import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trackPurchase, trackSubscribe } from '@/utils/metaPixel';
import { trackTwitterSubscription } from '@/utils/twitterPixel';
import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';

interface Subscription {
  id: string;
  status: string;
  plan_name: string;
  current_period_end: string | null;
}

interface SubscriptionBenefit {
  id: string;
  benefit_type: string;
  benefit_limit: number | null;
  used_count: number;
}

interface UseSubscriptionReturn {
  subscription: Subscription | null;
  benefits: SubscriptionBenefit[];
  hasActiveSubscription: boolean;
  isLoading: boolean;
  checkBenefitLimit: (benefitType: string) => { allowed: boolean; remaining: number };
  incrementBenefitUsage: (benefitType: string) => Promise<void>;
}

export function useSubscription(user: User | null): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [benefits, setBenefits] = useState<SubscriptionBenefit[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadSubscription = useCallback(async (includePending = false) => {
    if (!user) return;

    try {
      setIsLoading(true);

      // Load subscription - check for active/trialing first, but if includePending is true, check any status
      let query = supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (!includePending) {
        query = query.in('status', ['active', 'trialing']);
      }

      const { data: subData, error: subError } = await query.maybeSingle();

      if (subError && subError.code !== 'PGRST116') {
        console.error('Error loading subscription:', subError);
      }

      // Only set subscription if it's active or trialing (or if we're checking pending)
      if (subData && (includePending || subData.status === 'active' || subData.status === 'trialing')) {
        setSubscription(subData);

        // Load benefits only if subscription is active/trialing
        if (subData.status === 'active' || subData.status === 'trialing') {
          const { data: benefitsData, error: benefitsError } = await supabase
            .from('subscription_benefits')
            .select('*')
            .eq('subscription_id', subData.id)
            .order('benefit_type', { ascending: true });

          if (benefitsError) {
            console.error('Error loading benefits:', benefitsError);
            setBenefits([]);
          } else if (benefitsData && benefitsData.length > 0) {
            setBenefits(benefitsData);
          } else {
            // No benefits found - this shouldn't happen, but set empty array
            console.warn(`No benefits found for subscription ${subData.id}. Benefits should be created automatically.`);
            setBenefits([]);
          }
        }
      } else if (subData && !includePending) {
        // Subscription exists but not active - clear it
        setSubscription(null);
        setBenefits([]);
      } else if (!subData && !includePending) {
        // No subscription found - try to sync from Polar once per session
        // Use a flag in sessionStorage to track if we've already attempted sync this session
        const syncAttempted = sessionStorage.getItem(`subscription_sync_attempted_${user.id}`);
        
        if (!syncAttempted) {
          sessionStorage.setItem(`subscription_sync_attempted_${user.id}`, 'true');
          
          // Run sync in background without blocking UI
          (async () => {
            try {
              const { data: { session } } = await supabase.auth.getSession();
              if (session) {
                const SUPABASE_URL = 'https://ancujowsxivyfnxhnsmr.supabase.co';
                const syncResponse = await fetch(`${SUPABASE_URL}/functions/v1/polar-sync-subscription`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`,
                  },
                });

                if (syncResponse.ok) {
                  const syncData = await syncResponse.json();
                  if (syncData.success && syncData.subscription) {
                    // Reload subscription after a short delay
                    setTimeout(() => {
                      loadSubscription(false);
                    }, 1000);
                  }
                } else {
                  // If sync fails, clear the flag so user can manually retry
                  sessionStorage.removeItem(`subscription_sync_attempted_${user.id}`);
                }
              }
            } catch (syncError) {
              console.error('Auto-sync failed (non-critical):', syncError);
              // Clear flag on error so user can manually retry
              sessionStorage.removeItem(`subscription_sync_attempted_${user.id}`);
            }
          })();
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadSubscription();
    } else {
      setIsLoading(false);
    }
  }, [user, loadSubscription]);

  // Compute hasActiveSubscription early so it can be used in effects
  const hasActiveSubscription = 
    subscription?.status === 'active' || 
    subscription?.status === 'trialing';

  // Set up real-time subscription for benefits updates
  useEffect(() => {
    if (!subscription || !hasActiveSubscription) return;

    const channel = supabase
      .channel(`subscription_benefits_${subscription.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'subscription_benefits',
          filter: `subscription_id=eq.${subscription.id}`,
        },
        (payload) => {
          // Refresh benefits when they're updated
          loadSubscription(false);
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('⚠️ Real-time subscription error, will use polling fallback');
        }
      });

    // Also set up polling as fallback (every 3 seconds)
    const pollInterval = setInterval(() => {
      loadSubscription(false);
    }, 3000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollInterval);
    };
  }, [subscription?.id, hasActiveSubscription, loadSubscription]);

  // Poll subscription when returning from checkout
  useEffect(() => {
    // Check URL parameter FIRST (before checking user) to catch it even if user isn't loaded yet
    const params = new URLSearchParams(window.location.search);
    const isReturningFromCheckout = params.get('subscription') === 'success';
    
    // If we're returning from checkout but user isn't loaded yet, wait for user
    if (isReturningFromCheckout && !user) {
      console.log('[Meta Pixel] 🔄 Returning from checkout - Waiting for user to load...');
      return; // Will re-run when user is loaded (user is in dependency array)
    }
    
    // If no checkout parameter, nothing to do
    if (!isReturningFromCheckout) {
      return;
    }
    
    // User is loaded and we're returning from checkout - start tracking
    if (user) {
      console.log('[Meta Pixel] 🔄 Returning from checkout - Starting subscription polling...');
      console.log('[Meta Pixel] 📋 URL params:', Object.fromEntries(params.entries()));
      console.log('[Meta Pixel] 👤 User loaded:', user.id);
      
      // Remove the query parameter from URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      
      // Set loading state immediately
      setIsLoading(true);
      
      // Poll subscription data with exponential backoff
      let attempts = 0;
      const maxAttempts = 20; // Increased attempts
      const baseInterval = 1500; // Start with 1.5 seconds
      let timeoutId: number | null = null;
      let isPolling = true;

      const pollSubscription = async () => {
        if (!isPolling || attempts >= maxAttempts) {
          if (attempts >= maxAttempts) {
            console.warn('[Meta Pixel] ⚠️ Max polling attempts reached - subscription status may not be updated yet');
            setIsLoading(false);
          }
          return;
        }

        attempts++;
        console.log(`[Meta Pixel] 🔍 Polling subscription status (attempt ${attempts}/${maxAttempts})...`);
        
        // Check for subscription with any status (including pending)
        const { data: subData, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        if (subError && subError.code !== 'PGRST116') {
          console.error('[Meta Pixel] ❌ Error checking subscription:', subError);
        }
        
        if (subData) {
          console.log(`[Meta Pixel] 📊 Subscription found:`, {
            id: subData.id,
            status: subData.status,
            created_at: subData.created_at
          });
        } else {
          console.log('[Meta Pixel] ⏳ No subscription found yet, continuing to poll...');
        }
        
        // If we found a subscription and it's active/trialing, we're done
        if (subData && (subData.status === 'active' || subData.status === 'trialing')) {
          isPolling = false;
          
          // Track purchase/subscribe event ONLY when:
          // 1. Status is confirmed 'active' or 'trialing' (payment successful)
          // 2. This is a NEW subscription activation (not a renewal)
          // 3. We haven't tracked this subscription ID before
          const purchaseTracked = sessionStorage.getItem(`purchase_tracked_${subData.id}`);
          const subscriptionCreated = subData.created_at ? new Date(subData.created_at).getTime() : 0;
          const isNewSubscription = subscriptionCreated > 0 && (Date.now() - subscriptionCreated) < 24 * 60 * 60 * 1000; // Created within last 24 hours
          
          // Debug logging to help diagnose issues
          console.log('[Meta Pixel] 🔍 Checking subscription tracking conditions:', {
            subscriptionId: subData.id,
            status: subData.status,
            purchaseTracked: !!purchaseTracked,
            isNewSubscription: isNewSubscription,
            subscriptionAge: subscriptionCreated > 0 ? Math.round((Date.now() - subscriptionCreated) / 1000 / 60) : 0, // minutes
            created_at: subData.created_at
          });
          
          if (!purchaseTracked && isNewSubscription) {
            // Convert price from cents to dollars if needed (Polar stores in cents)
            // If price is > 100, assume it's in cents and convert
            const rawPrice = subData.plan_price_amount || 8790; // Default in cents: $87.90
            const price = rawPrice > 100 ? rawPrice / 100 : rawPrice; // Convert cents to dollars if needed
            const currency = subData.plan_price_currency || 'USD';
            
            // CRITICAL: Only track Purchase/Subscribe when payment is confirmed
            // Status 'active' or 'trialing' means Polar has confirmed successful payment
            // This prevents tracking for checkout attempts that don't result in payment
            console.log('[Meta Pixel] 🎯 Tracking Purchase/Subscribe event - Payment confirmed!', {
              subscriptionId: subData.id,
              status: subData.status,
              rawPrice: rawPrice,
              price: price,
              currency: currency
            });
            
            // Retry mechanism to ensure pixel is loaded before tracking
            let retryCount = 0;
            const maxRetries = 10; // Try for up to 5 seconds (10 * 500ms)
            
            const attemptTracking = () => {
              if (typeof window.fbq === 'undefined') {
                retryCount++;
                if (retryCount < maxRetries) {
                  console.log(`[Meta Pixel] ⏳ Pixel not loaded yet, retrying... (${retryCount}/${maxRetries})`);
                  setTimeout(attemptTracking, 500); // Retry every 500ms
                } else {
                  console.error('[Meta Pixel] ❌ Pixel failed to load after multiple retries. Events not sent.');
                  console.error('[Meta Pixel] 💡 This may be due to ad blocker or network issues.');
                  // Still mark as tracked to prevent infinite retries
                  sessionStorage.setItem(`purchase_tracked_${subData.id}`, 'true');
                }
                return;
              }
              
              // Pixel is loaded, fire events
              console.log('[Meta Pixel] 🎉 REAL SUBSCRIPTION DETECTED - Firing Purchase/Subscribe events!');
              console.log('[Meta Pixel] 📊 Subscription details:', {
                id: subData.id,
                status: subData.status,
                price: price,
                currency: currency,
                created_at: subData.created_at
              });
              
              trackPurchase({
                value: price,
                currency: currency,
                content_name: 'Subscription',
                content_category: 'Subscription',
                num_items: 1
              });
              
              trackSubscribe({
                value: price,
                currency: currency,
                content_name: 'Subscription',
                content_category: 'Subscription'
              });
              
              // Track Twitter/X Pixel subscription conversion
              trackTwitterSubscription({
                value: price,
                currency: currency,
                conversion_id: subData.id,
                email_address: user.email || undefined
              });
              
              // Mark as tracked to prevent duplicate events
              sessionStorage.setItem(`purchase_tracked_${subData.id}`, 'true');
              sessionStorage.setItem(`twitter_subscription_tracked_${subData.id}`, 'true');
              console.log('[Meta Pixel] ✅ Purchase/Subscribe events tracked successfully for REAL subscription');
              console.log('[Twitter Pixel] ✅ Subscription conversion event tracked successfully');
              console.log('[Meta Pixel] 💡 Check Network tab → filter "facebook.com/tr" to verify requests were sent');
              console.log('[Twitter Pixel] 💡 Check Network tab → filter "ads-twitter.com" to verify requests were sent');
              console.log('[Meta Pixel] 💡 Check Meta Events Manager → Test Events tab (real-time)');
              console.log('[Twitter Pixel] 💡 Check Twitter Ads Manager → Events Manager');
            };
            
            // Start tracking attempt after initial delay
            setTimeout(attemptTracking, 100);
          } else {
            console.warn('[Meta Pixel] ⚠️ Skipping Purchase/Subscribe tracking:', {
              reason: purchaseTracked ? 'Already tracked' : !isNewSubscription ? 'Not a new subscription' : 'Unknown',
              purchaseTracked: !!purchaseTracked,
              isNewSubscription: isNewSubscription
            });
          }
          
          // Reload subscription to get full data including benefits
          await loadSubscription(false);
          setIsLoading(false);
          return; // Stop polling - we're done
        }
        
        // Subscription not active yet, continue polling
        if (subData && subData.status !== 'active' && subData.status !== 'trialing') {
          console.log(`[Meta Pixel] ⏳ Subscription status is '${subData.status}' - waiting for Polar webhook to update to 'active'...`);
        }
        
        // Continue polling if we haven't reached max attempts
        if (attempts < maxAttempts) {
          const delay = Math.min(baseInterval * Math.pow(1.2, attempts - 1), 10000); // Max 10 seconds
          console.log(`[Meta Pixel] ⏱️ Waiting ${Math.round(delay)}ms before next poll...`);
          timeoutId = setTimeout(pollSubscription, delay) as unknown as number;
        } else {
          console.warn('[Meta Pixel] ⚠️ Max polling attempts reached. Subscription may still be processing.');
          console.warn('[Meta Pixel] 💡 Subscription status may update via webhook. Check again in a few moments.');
          setIsLoading(false);
        }
      };

      // Start polling after initial delay
      const initialDelay = 1000; // Wait 1 second before first poll
      console.log(`[Meta Pixel] ⏱️ Starting subscription polling in ${initialDelay}ms...`);
      timeoutId = setTimeout(pollSubscription, initialDelay) as unknown as number;

      // Cleanup on unmount
      return () => {
        isPolling = false;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [user, loadSubscription]);

  // Trigger first analysis automatically for new subscribers
  useEffect(() => {
    if (!subscription || !user) return;
    
    const triggerAutoAnalysis = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // Get user's primary website
          const { data: website } = await supabase
            .from('websites')
            .select('id, domain')
            .eq('user_id', user.id)
            .eq('is_primary', true)
            .single();

          if (website) {
            // Check if analysis already exists or is running
            const { data: existingAnalyses } = await supabase
              .from('website_analysis')
              .select('id')
              .eq('website_id', website.id)
              .limit(1)
              .maybeSingle();

            // Also check for running jobs
            const { data: runningJob } = await supabase
              .from('website_analysis_jobs')
              .select('id')
              .eq('website_id', website.id)
              .in('status', ['processing', 'queued', 'pending'])
              .limit(1)
              .maybeSingle();

            // Only auto-run if no analysis exists yet and no job is running
            if (!existingAnalyses && !runningJob) {
              console.log('Auto-running first analysis for new subscription:', website.domain);
              // Trigger analysis via edge function
              await supabase.functions.invoke('enhanced-firecrawl-gemini-v2', {
                body: {
                  website_url: `https://${website.domain}`,
                  user_id: user.id,
                  website_id: website.id
                }
              });
              console.log('✅ Auto-analysis triggered successfully');
            }
          }
        }
      } catch (error) {
        console.error('Error triggering auto-analysis:', error);
        // Don't show error to user - analysis will be triggered by webhook anyway
      }
    };

    triggerAutoAnalysis();
  }, [subscription, user]);

  const checkBenefitLimit = (benefitType: string): { allowed: boolean; remaining: number } => {
    // First check if subscription is active
    if (!hasActiveSubscription) {
      return { allowed: false, remaining: 0 };
    }

    const benefit = benefits.find(b => b.benefit_type === benefitType);
    
    if (!benefit) {
      // No benefit found - user has subscription but no benefit configured
      return { allowed: false, remaining: 0 };
    }

    if (benefit.benefit_limit === null) {
      // Unlimited
      return { allowed: true, remaining: -1 }; // -1 means unlimited
    }

    const remaining = benefit.benefit_limit - benefit.used_count;
    return {
      allowed: remaining > 0,
      remaining: Math.max(0, remaining),
    };
  };

  const incrementBenefitUsage = async (benefitType: string): Promise<void> => {
    // Check if subscription is active before allowing usage
    if (!hasActiveSubscription) {
      throw new Error('Active subscription required to use this feature');
    }

    if (!subscription) {
      throw new Error('No subscription found');
    }

    const benefit = benefits.find(b => b.benefit_type === benefitType);
    if (!benefit) {
      throw new Error(`Benefit ${benefitType} not found`);
    }

    // Check if we can increment
    if (benefit.benefit_limit !== null && benefit.used_count >= benefit.benefit_limit) {
      throw new Error(`Limit reached for ${benefitType}`);
    }

    // Update in database
    const { error } = await supabase
      .from('subscription_benefits')
      .update({
        used_count: benefit.used_count + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', benefit.id);

    if (error) {
      console.error('Error incrementing benefit usage:', error);
      throw error;
    }

    // Update local state
    setBenefits(prev => 
      prev.map(b => 
        b.benefit_type === benefitType 
          ? { ...b, used_count: b.used_count + 1 }
          : b
      )
    );
  };

  return {
    subscription,
    benefits,
    hasActiveSubscription,
    isLoading,
    checkBenefitLimit,
    incrementBenefitUsage,
  };
}

